import { useEffect, type FC, type ReactElement } from 'react';

import type { Runtime } from '@xing-com/crate-xinglet/internal';
import { RuntimeProvider } from '@xing-com/crate-xinglet/internal';

const LoadingStateHandler: FC<{ runtime: Runtime }> = ({ runtime }) => {
  useEffect(() => {
    let counter = 0;
    const mountpoint = document.querySelector('#app[data-mountpoint]');
    const waitingFor = new Set(
      [...runtime.cachedXinglets.entries()].flatMap(([name, { settled }]) => {
        return settled ? [] : [name];
      })
    );

    if (waitingFor.size === 0) {
      mountpoint?.setAttribute('data-xinglets-settled', `${++counter}`);
    }

    function xingletAwaited(name: string): void {
      waitingFor.add(name);
    }

    function xingletRendered(name: string): void {
      if (waitingFor.delete(name) && waitingFor.size === 0) {
        mountpoint?.setAttribute('data-xinglets-settled', `${++counter}`);
      }
    }

    runtime.events.on('xinglet-awaited', xingletAwaited);
    runtime.events.on('xinglet-rendered', xingletRendered);

    return () => {
      runtime.events.off('xinglet-awaited', xingletAwaited);
      runtime.events.off('xinglet-rendered', xingletRendered);
    };
  });

  return <></>;
};

export function createEntryPoint(
  runtime: Runtime,
  name: string,
  props: Record<string, unknown> = {}
): ReactElement {
  return (
    <RuntimeProvider runtime={runtime}>
      <LoadingStateHandler runtime={runtime} />
      <runtime.host.XingletLoader {...props} runtime={runtime} name={name} />
    </RuntimeProvider>
  );
}
